import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";
import { Deal } from "./deal";

const BASE_URL = `${API_BASE_PATH}/invoiceproposal`;

// Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// Error handler
const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  toast.error(detail);
  console.error(detail);
};

// Invoice interface
export interface Invoice {
  invoice_proposal_id?: string;
  invoice_date: string;
  Date_of_proposal?: string | null;
  validation_date: string;
  status: string;
  signature_name?: string | null;
  notes?: string | null;
  termsandconditions?: string | null;
  subtotalamount?: number | null;
  taxtotalamount?: number | null;
  invoice_no_logins?: number | null;
  invoice_rate_per_login?: number | null;
  invoice_tax?: number | null;
  invoice_amount?: number | null;
  invoice_total_amount?: number | null;
  discount?: number | null;
  proposal_tax_percentage?: number | null;
  proposal_discount_percentage?: number | null;
  gst_number?: string | null;
  client_address?: string | null;
  client_city?: string | null;
  client_state?: string | null;
  client_zipcode?: string | null;
  client_country?: string | null;
  cgst?: number | null;
  sgst?: number | null;
  igst?: number | null;
  cgst_percentage?: number | null;
  sgst_percentage?: number | null;
  igst_percentage?: number | null;
  amc?: string | null;
  created_at?: string;
  updated_at?: string;
deal: {
    deal_id: string;
    deal_value?: number | null; 
    currency: string;
    product: {
      product_id: string;
      product_name: string;
      product_price: number;
    };
    contact: {
      contact_id?: string;
      contact_name?: string | null;
      contact_persona_name?: string | null;
      company_name?: string;
      company_mail?: string;
      contact_person_name?: string;
      company_phone?: string;
      users?: {
        id: string;
        name: string;
        phone_no?: string;
        email?: string;
      } | null;
      branch?: Array<{
        branch_id?: string;
        branch_name?: string;
        branch_address?: string;
        branch_city?: string;
        branch_state?: string;
        branch_zipcode?: string;
        branch_country?: string;
        gst_number?: string;
      }>;
    };
    leads?: {
      no_logins?: number;
      rate_per_login?: number;
      contact?: {
        contact_name?: string | null;
        contact_persona_name?: string | null;
        company_phone?: string;
        company_mail?: string;
        branch?: Array<{
          branch_id?: string;
          branch_name?: string;
          branch_address?: string;
          branch_city?: string;
          branch_state?: string;
          branch_zipcode?: string;
          branch_country?: string;
          gst_number?: string;
        }>;
      };
    } | null;
  };
}

// Helper to convert Invoice object to FormData
const toFormData = (data: Record<string, any>): FormData => {
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (key === "attachment" && value instanceof File) {
        form.append("attachment", value);
      } else if (typeof value === "object" && value !== null) {
        form.append(key, JSON.stringify(value)); // For nested objects
      } else {
        form.append(key, value.toString());
      }
    }
  });
  return form;
};

// ✅ Create Invoice
export const createInvoice = async (data: Invoice): Promise<Invoice> => {
  try {
    const formData = toFormData(data);
    const res = await axios.post<Invoice>(`${BASE_URL}/create`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...getAuthHeaders(),
      },
    });
    toast.success("Invoice created successfully");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to create Invoice");
    throw error;
  }
};

// ✅ Fetch All Invoices
export const fetchAllInvoices = async (): Promise<Invoice[]> => {
  try {
    const res = await axios.get<Invoice[]>(`${BASE_URL}/getall/`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch Invoices");
    throw error;
  }
};

// ✅ Fetch Single Invoice
export const fetchInvoiceById = async (
  invoice_proposal_id: string
): Promise<Invoice> => {
  try {
    const res = await axios.get<Invoice>(
      `${BASE_URL}/get/single/${invoice_proposal_id}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch Invoice");
    throw error;
  }
};

// ✅ Delete Invoice
export const deleteInvoice = async (
  invoice_proposal_id: string
): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/delete/${invoice_proposal_id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Invoice deleted successfully");
  } catch (error) {
    showAxiosError(error, "Failed to delete Invoice");
    throw error;
  }
};

export const deleteInvoiceProposalsBulk = async (invoiceIds: string[]): Promise<void> => {
  try {
    if (!invoiceIds || invoiceIds.length === 0) return;

    const idsParam = invoiceIds.join(",");

    await axios.delete(
      `${BASE_URL}-bulk-delete?invoice_ids=${encodeURIComponent(idsParam)}`,
      {
        headers: getAuthHeaders(),
      }
    );

    toast.success("Invoice proposals deleted successfully");
  } catch (error) {
    showAxiosError(error, "Failed to delete invoice proposals");
    throw error;
  }
};
// Fetch Invoices by Lead Owner
export const fetchInvoicesByLeadOwner = async (
  user_id: string
): Promise<Invoice[]> => {
  try {
    const res = await axios.get<Invoice[]>(
      `${BASE_URL}/get/by-leadowner/${user_id}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch Invoices by lead owner");
    throw error;
  }
};